/**
 * Evals — answer-quality scoring and the zero-leakage compliance gate.
 *
 * Two panels:
 *   1. Answer quality — pick a dataset + metrics, run, and see the metric
 *      summary plus per-question scores. Runs execute as the current role, so
 *      scores reflect what that role actually sees.
 *   2. Zero-leakage gate — run adversarial queries as an uncleared role and get
 *      a prominent PASS/FAIL: no ITAR/CUI content may ever reach an uncleared
 *      role. This is the promotion/compliance gate.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import {
  fetchDatasets,
  fetchMetrics,
  runEval,
  runLeakageCheck,
  type EvalMetric,
  type EvalRunResult,
  type LeakageResult,
} from '../lib/evals';

function fmtScore(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toFixed(2);
}

function MetricSummary({ result }: { result: EvalRunResult }): ReactNode {
  const entries = Object.entries(result.summary);
  return (
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {entries.map(([name, value]) => (
        <div key={name} className="rounded-lg border border-steel-200 bg-white p-3">
          <div className="text-2xl font-semibold text-navy-900">{fmtScore(value)}</div>
          <div className="text-xs text-steel-500">{name}</div>
        </div>
      ))}
    </div>
  );
}

export function Evals(): ReactNode {
  const metricsQuery = useQuery({ queryKey: ['eval-metrics'], queryFn: fetchMetrics });
  const datasetsQuery = useQuery({ queryKey: ['eval-datasets'], queryFn: fetchDatasets });

  const [datasetId, setDatasetId] = useState<string>('');
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(new Set());

  // Default selection: the reference-free metrics (no golden answer needed).
  useEffect(() => {
    if (metricsQuery.data && selectedMetrics.size === 0) {
      setSelectedMetrics(
        new Set(metricsQuery.data.filter((m) => !m.requires_golden).map((m) => m.name)),
      );
    }
  }, [metricsQuery.data, selectedMetrics.size]);

  // Default dataset: the first one.
  useEffect(() => {
    const first = datasetsQuery.data?.[0];
    if (first && !datasetId) {
      setDatasetId(first.id);
    }
  }, [datasetsQuery.data, datasetId]);

  const evalRun = useMutation<EvalRunResult, ApiError, void>({
    mutationFn: () => runEval({ dataset_id: datasetId, metrics: [...selectedMetrics] }),
  });

  const leakage = useMutation<LeakageResult, ApiError, void>({
    mutationFn: () => runLeakageCheck(),
  });

  function toggleMetric(name: string): void {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const selectedDataset = datasetsQuery.data?.find((d) => d.id === datasetId);
  const canRun = Boolean(datasetId) && selectedMetrics.size > 0 && !evalRun.isPending;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 animate-fade-in">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">Evaluations</h2>
        <p className="mt-1 text-sm text-steel-600">
          Score answer quality and run the zero-leakage compliance gate. Every run
          executes through the permission filter, so results reflect what a role
          is entitled to see.
        </p>
      </header>

      {/* ---------------- Zero-leakage gate (the headline) ---------------- */}
      <section className="card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-navy-900">Zero-leakage gate</h3>
            <p className="mt-1 text-sm text-steel-600">
              Runs adversarial queries as an uncleared role and asserts no ITAR/CUI
              content is ever returned. This is the promotion/compliance gate.
            </p>
          </div>
          <button
            type="button"
            data-testid="run-leakage"
            disabled={leakage.isPending}
            onClick={() => leakage.mutate()}
            className="shrink-0 rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {leakage.isPending ? 'Running…' : 'Run gate'}
          </button>
        </div>

        {leakage.error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {leakage.error.message}
          </p>
        )}

        {leakage.data && (
          <div className="mt-4">
            <div
              role="status"
              className={`rounded-lg border p-4 ${
                leakage.data.passed
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              <div className="text-lg font-semibold">
                {leakage.data.passed ? 'PASS — no leakage' : 'FAIL — leakage detected'}
              </div>
              <div className="mt-1 text-sm">
                {leakage.data.queries_checked} adversarial queries checked ·{' '}
                {leakage.data.restricted_candidates_blocked} restricted candidates blocked ·
                guarding {leakage.data.forbidden_sensitivities.join(', ')}
              </div>
            </div>
            {leakage.data.findings.length > 0 && (
              <ul className="mt-3 space-y-2">
                {leakage.data.findings.map((f, i) => (
                  <li key={i} className="rounded-lg border border-red-200 bg-white p-3 text-sm">
                    <div className="font-medium text-navy-900">{f.query}</div>
                    <div className="text-xs text-red-700">
                      leaked: {f.leaked.map((l) => `${l.title ?? '?'} [${l.sensitivity.join('/')}]`).join(', ')}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* ---------------- Answer-quality eval ---------------- */}
      <section className="card">
        <h3 className="text-base font-semibold text-navy-900">Answer quality</h3>

        <div className="mt-4 flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-steel-600">Dataset</span>
            <select
              value={datasetId}
              onChange={(e) => setDatasetId(e.target.value)}
              disabled={datasetsQuery.isLoading}
              className="rounded-lg border border-steel-200 px-3 py-2 text-sm text-navy-900 focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-500"
            >
              {(datasetsQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.row_count} rows{d.has_golden ? ', has golden' : ''})
                </option>
              ))}
            </select>
            {selectedDataset && (
              <span className="text-xs text-steel-500">{selectedDataset.description}</span>
            )}
          </label>

          <fieldset className="flex flex-col gap-2">
            <span className="text-xs font-medium text-steel-600">Metrics</span>
            <div className="flex flex-wrap gap-2">
              {(metricsQuery.data ?? []).map((m: EvalMetric) => {
                const disabled = m.requires_golden && !selectedDataset?.has_golden;
                return (
                  <label
                    key={m.name}
                    title={m.description}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
                      disabled ? 'border-steel-100 text-steel-300' : 'border-steel-200 text-navy-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMetrics.has(m.name)}
                      disabled={disabled}
                      onChange={() => toggleMetric(m.name)}
                    />
                    {m.display_name}
                    {m.requires_golden && <span className="text-[10px] text-steel-400">(golden)</span>}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <button
            type="button"
            data-testid="run-eval"
            disabled={!canRun}
            onClick={() => evalRun.mutate()}
            className="self-start rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {evalRun.isPending ? 'Scoring…' : 'Run eval'}
          </button>

          {evalRun.error && (
            <p role="alert" className="text-sm text-red-700">
              {evalRun.error.message}
            </p>
          )}

          {evalRun.data && (
            <div>
              <MetricSummary result={evalRun.data} />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-steel-200 text-xs uppercase text-steel-500">
                      <th className="py-2 pr-3">Question</th>
                      <th className="px-3">Returned</th>
                      {evalRun.data.metrics.map((m) => (
                        <th key={m} className="px-3">{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {evalRun.data.rows.map((row, i) => (
                      <tr key={i} className="border-b border-steel-100 align-top">
                        <td className="py-2 pr-3 text-navy-800">{row.question}</td>
                        <td className="px-3 text-steel-600">{row.returned_count}</td>
                        {evalRun.data!.metrics.map((m) => (
                          <td key={m} className="px-3 font-mono text-steel-700">
                            {fmtScore(row.scores[m])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Evals;
